// Landschaft um die Spielfläche: flache Mitte, nach außen Hügel, Wald und Berge, die im Nebel auslaufen.
import * as THREE from "three"
import { rng } from "../game/rng.js"

export const FLAT_RADIUS = 64
const BLEND_END = 112
const SIZE = 560
const SEGMENTS = 140

export const LAKE = { x: 92, z: 52, r: 13, level: -0.7 }
export const PEAK = { x: 18, z: -132, r: 38, height: 38 }
const RANGE = [
  { x: -150, z: -95, r: 55, height: 30 },
  { x: 150, z: -60, r: 50, height: 26 },
  { x: -120, z: 140, r: 45, height: 20 }
]

const GROUND_COLORS = {
  grass: new THREE.Color(0x9fd18b),
  meadow: new THREE.Color(0x8cc47a),
  forest: new THREE.Color(0x76ad66),
  rock: new THREE.Color(0x9a958a),
  rockDark: new THREE.Color(0x7d786f),
  snow: new THREE.Color(0xf4f4f0),
  sand: new THREE.Color(0xd9cf9f)
}

// --- Rauschen -------------------------------------------------------------

function hash(x, z) {
  const s = Math.sin(x * 127.1 + z * 311.7) * 43758.5453
  return s - Math.floor(s)
}

function valueNoise(x, z) {
  const xi = Math.floor(x)
  const zi = Math.floor(z)
  const xf = x - xi
  const zf = z - zi
  const u = xf * xf * (3 - 2 * xf)
  const v = zf * zf * (3 - 2 * zf)
  const a = hash(xi, zi)
  const b = hash(xi + 1, zi)
  const c = hash(xi, zi + 1)
  const d = hash(xi + 1, zi + 1)
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v
}

function fbm(x, z, octaves = 4) {
  let sum = 0
  let amplitude = 0.5
  let frequency = 1
  for (let i = 0; i < octaves; i++) {
    sum += valueNoise(x * frequency, z * frequency) * amplitude
    amplitude *= 0.5
    frequency *= 2.1
  }
  return sum / 0.9375
}

const smoothstep = (edge0, edge1, x) => {
  const t = THREE.MathUtils.clamp((x - edge0) / (edge1 - edge0), 0, 1)
  return t * t * (3 - 2 * t)
}

// Höhe des Geländes an (x, z). Innerhalb der Spielfläche immer 0.
export function heightAt(x, z) {
  const r = Math.hypot(x, z)
  const blend = smoothstep(FLAT_RADIUS, BLEND_END, r)
  if (blend <= 0) return 0

  const hills = fbm(x * 0.024 + 3.1, z * 0.024 - 1.7) * (11 + smoothstep(90, 200, r) * 26)
  const ridges = (1 - Math.abs(fbm(x * 0.012, z * 0.012, 3) * 2 - 1)) * smoothstep(120, 240, r) * 30
  let h = blend * (hills + ridges - 3)

  for (const mountain of [PEAK, ...RANGE]) {
    const d = Math.hypot(x - mountain.x, z - mountain.z)
    h += blend * Math.exp(-(d * d) / (2 * mountain.r * mountain.r * 0.3)) * mountain.height
  }

  const lake = Math.hypot(x - LAKE.x, z - LAKE.z)
  const carve = smoothstep(LAKE.r + 9, LAKE.r - 3, lake)
  h = h * (1 - carve) + (LAKE.level - 2.2) * carve
  return h
}

function slopeAt(x, z) {
  const e = 1.5
  return Math.hypot(heightAt(x + e, z) - heightAt(x - e, z), heightAt(x, z + e) - heightAt(x, z - e)) / (2 * e)
}

function groundColor(x, z, h, slope) {
  const r = Math.hypot(x, z)
  if (Math.hypot(x - LAKE.x, z - LAKE.z) < LAKE.r + 2.5 && h < 0.4) return GROUND_COLORS.sand
  if (h > 34 - slope * 6) return GROUND_COLORS.snow
  if (slope > 0.75 || h > 24) return slope > 1.1 ? GROUND_COLORS.rockDark : GROUND_COLORS.rock
  // Wiesenflecken überall (auch in der Spielfläche), damit kein Rand als Kreis sichtbar ist
  const patch = fbm(x * 0.04 - 7, z * 0.04 + 2, 2)
  const forested = r > FLAT_RADIUS + 2 && fbm(x * 0.05 + 11, z * 0.05 - 4, 2) > 0.5
  if (forested) return GROUND_COLORS.forest
  return patch > 0.55 ? GROUND_COLORS.meadow : GROUND_COLORS.grass
}

export function buildTerrain() {
  const geometry = new THREE.PlaneGeometry(SIZE, SIZE, SEGMENTS, SEGMENTS).toNonIndexed()
  geometry.rotateX(-Math.PI / 2)
  const position = geometry.attributes.position
  for (let i = 0; i < position.count; i++) {
    position.setY(i, heightAt(position.getX(i), position.getZ(i)))
  }
  // Eine Farbe pro Dreieck für den Low-Poly-Look
  const colors = new Float32Array(position.count * 3)
  for (let i = 0; i < position.count; i += 3) {
    const cx = (position.getX(i) + position.getX(i + 1) + position.getX(i + 2)) / 3
    const cz = (position.getZ(i) + position.getZ(i + 1) + position.getZ(i + 2)) / 3
    const cy = (position.getY(i) + position.getY(i + 1) + position.getY(i + 2)) / 3
    const color = groundColor(cx, cz, cy, slopeAt(cx, cz))
    for (let k = 0; k < 3; k++) color.toArray(colors, (i + k) * 3)
  }
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3))
  geometry.computeVertexNormals()

  const terrain = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 1 }))
  terrain.receiveShadow = true

  const water = new THREE.Mesh(
    new THREE.CircleGeometry(LAKE.r + 4, 24),
    new THREE.MeshStandardMaterial({ color: 0x5aa9d6, roughness: 0.25, metalness: 0.1, transparent: true, opacity: 0.88, flatShading: true })
  )
  water.rotation.x = -Math.PI / 2
  water.position.set(LAKE.x, LAKE.level, LAKE.z)
  water.receiveShadow = true

  const group = new THREE.Group()
  group.add(terrain, water)
  return group
}

// Wald außerhalb der Spielfläche als zwei InstancedMeshes (Stamm + Krone).
export function buildForest({ count = 900, keepOut = [] } = {}) {
  const random = rng(4242)
  const trunks = new THREE.InstancedMesh(
    new THREE.CylinderGeometry(0.15, 0.2, 1, 5).translate(0, 0.5, 0),
    new THREE.MeshStandardMaterial({ color: 0x7a5a3a, flatShading: true, roughness: 1 }),
    count
  )
  const crowns = new THREE.InstancedMesh(
    new THREE.ConeGeometry(0.9, 2.4, 6).translate(0, 1.9, 0),
    new THREE.MeshStandardMaterial({ color: 0xffffff, flatShading: true, roughness: 1 }),
    count
  )
  const shades = [0x4f9a5b, 0x5aa65f, 0x3f8a52, 0x6db36a, 0x47905a].map((c) => new THREE.Color(c))
  const matrix = new THREE.Matrix4()
  const quaternion = new THREE.Quaternion()
  const scale = new THREE.Vector3()
  let placed = 0
  for (let attempt = 0; attempt < count * 12 && placed < count; attempt++) {
    const angle = random() * Math.PI * 2
    const radius = FLAT_RADIUS + 4 + Math.pow(random(), 0.8) * 150
    const x = Math.cos(angle) * radius
    const z = Math.sin(angle) * radius
    const h = heightAt(x, z)
    const density = fbm(x * 0.05 + 11, z * 0.05 - 4, 2)
    if (density < 0.47 || h > 22 || h < 0.2 || slopeAt(x, z) > 0.7) continue
    if (keepOut.some((area) => Math.hypot(x - area.x, z - area.z) < area.r)) continue
    const s = 1.1 + random() * 1.3
    quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), random() * Math.PI)
    scale.set(s, s * (0.9 + random() * 0.4), s)
    matrix.compose(new THREE.Vector3(x, h - 0.1, z), quaternion, scale)
    trunks.setMatrixAt(placed, matrix)
    crowns.setMatrixAt(placed, matrix)
    crowns.setColorAt(placed, shades[Math.floor(random() * shades.length)])
    placed++
  }
  trunks.count = placed
  crowns.count = placed
  const group = new THREE.Group()
  group.add(trunks, crowns)
  return group
}
